import React, { useState } from 'react';
import Button from 'components/Button/Button';
import Input from 'components/Input/Input';
import StarRating from 'components/StarRating/StarRating';
import { t } from 'helpers/componentHelper';
import { CONTACT_FIELDS } from 'helpers/constantsHelper';
import { submitContact } from 'services/contactApi';

import './Contact.scss';

function Contact() {
  const [fieldMap, setFieldMap] = useState({});

  const onSubmitHandler = () => {
    // validate every required field is present. The CONTACT_FIELDS
    // map carries (id, required) but its `id` for `message` is the
    // string 'phone' (pre-existing quirk in constantsHelper.ts), so we
    // read the textarea value from that key when building the payload.
    let valid = true;
    Object.keys(CONTACT_FIELDS).forEach((key) => {
      if (CONTACT_FIELDS[key].required && !fieldMap[CONTACT_FIELDS[key].id]) {
        valid = false;
      }
    });
    if (!valid) return;

    const ratingRaw = fieldMap[CONTACT_FIELDS.rating.id];
    const rating = ratingRaw ? Number(ratingRaw) : undefined;

    submitContact({
      firstname: fieldMap[CONTACT_FIELDS.firstname.id] ?? '',
      email: fieldMap[CONTACT_FIELDS.email.id] ?? '',
      message: fieldMap[CONTACT_FIELDS.message.id] ?? '',
      rating: Number.isFinite(rating) ? rating : undefined,
    }).catch(() => {
      // Best-effort — server-side validation errors surface via the
      // per-field errorText prop, network errors stay quiet.
    });
  };

  const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFieldMap({ ...fieldMap, [event.currentTarget?.id]: event.currentTarget?.value });
  };

  return (
    <div className={'contact'}>
      <div className={'contact__header'}>
        <div>{`${t('pages.contact.header')}`}</div>
        <div className={'contact__header--subtext'}>{`${t('pages.contact.headerText')}`}</div>
      </div>
      <div className={'contact__flex--row'}>
        <div>
          <label>{`${t('pages.contact.fields.firstN')}`}</label>
          <div>
            <Input
              id={CONTACT_FIELDS.firstname.id}
              value={fieldMap[CONTACT_FIELDS.firstname.id]}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChangeHandler(event)}
              errorText={
                !fieldMap[CONTACT_FIELDS.firstname.id] ? `${t('pages.contact.required')}` : ''
              }
            />
          </div>
        </div>
      </div>
      <div>
        <label>{`${t('pages.contact.fields.email')}`}</label>
        <div>
          <Input
            id={CONTACT_FIELDS.email.id}
            value={fieldMap[CONTACT_FIELDS.email.id]}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChangeHandler(event)}
            errorText={!fieldMap[CONTACT_FIELDS.email.id] ? `${t('pages.contact.required')}` : ''}
          />
        </div>
      </div>
      <div>
        <label>{`${t('pages.contact.fields.message')}`}</label>
        <div>
          <Input
            id={CONTACT_FIELDS.message.id}
            value={fieldMap[CONTACT_FIELDS.message.id]}
            textArea={true}
            onTextAreaChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
              onChangeHandler(event)
            }
            errorText={!fieldMap[CONTACT_FIELDS.message.id] ? `${t('pages.contact.required')}` : ''}
          />
        </div>
      </div>
      <div>
        <label>{`Rate your experience`}</label>
        <div>
          <StarRating
            id={CONTACT_FIELDS.rating.id}
            stars={[1, 2, 3, 4, 5]}
            name="contactStars"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChangeHandler(event)}
          />
        </div>
      </div>

      <div className={'contact__flex--row-center'}>
        <div className={'contact__flex--row-center'}>
          <input id="agree" type={'checkbox'} checked={true} />
          <label className={'contact__footer'}>{`${t('pages.contact.fields.agree')}`}</label>
        </div>
        <div className={'contact__button'}>
          <Button text={`${t('app.buttons.submit')}`} onClick={() => onSubmitHandler()} />
        </div>
      </div>
    </div>
  );
}

export default Contact;
